import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import './ActionableIntelligence.scss';
import { ActionableAnswer, ActionableItem, AnswerSource, AssigneeType, DDX, Priority, Status } from '../../dashboard/llm/common/models';
import { ACTIONABLE_ANSWERS_BY_ITEM_ID, callQuery, GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE, PatientFileVersion } from '../../../amplify/api-gateway/query';
import { callWrite, EXPLAIN_DDX, INSERT_ACTIONABLE_ANSWER, SCAN_MULTI, UPDATE_ACTIONABLE_ITEM_STATUS, UPSERT_ACTIONABLE_ITEM } from '../../../amplify/api-gateway/write';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faArrowUpRightFromSquare, faCalendar, faChartLine, faCheck, faCheckCircle, faCircle, faClock, faCodeBranch, faComment, faComments, faExclamation, faExclamationCircle, faGear, faHospitalUser, faIdCard, faPencilAlt, faPlay, faQuestionCircle, faRobot, faS, faSpinner, faStethoscope, faTag, faTasks, faUndo, faUser, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import PersonalIdentifier from '../../common/PersonalIdentifier';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { PatientVisit } from '../../../amplify/api-gateway/search-patients';
import { LLMDashboardMarkdownRenderer } from '../../dashboard/llm/common/LLMDashboardMarkdownRenderer';
import { dashboardLLMCache } from '../../dashboard/llm/cache';


interface ActionableIntelligenceTrackerProps {
    tenant: string;
    patient: PatientVisit;
    items: ActionableItem[];
    setItems: (items: ActionableItem[]) => void;
}

const ActionableIntelligenceTracker = ({
    tenant,
    patient,
    items,
    setItems,
}: ActionableIntelligenceTrackerProps) => {
    const [selectedItem, setSelectedItem] = useState<ActionableItem | null>(null);
    const [ddx, setDDX] = useState<DDX>();
    const [scanMulti, setScanMulti] = useState();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [filter, setFilter] = useState<Status | 'all'>('OPEN');
    const [answers, setAnswers] = useState<ActionableAnswer[]>([]);
    const [loadingAnswers, setLoadingAnswers] = useState<boolean>(false);
    const [newAnswerText, setNewAnswerText] = useState<string>("");
    const [selectedAssignee, setSelectedAssignee] = useState<AssigneeType | null>(null);
    const [sortBy, setSortBy] = useState<'date' | 'priority' | 'type'>('date');

    const assigneeOptions: AssigneeType[] = ['MD', 'RN', 'SOCIAL_WORK', 'LIDIA'];

    useEffect(() => {
        // It's important to load the version of the file that the explain data
        // was generated from to ensure the citations line up
        //
        if (patient && showModal && selectedItem) {

            callQuery({
                operation: GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE,
                csn: patient.csn,
                file_type: EXPLAIN_DDX,
            })
            .then((versions: PatientFileVersion[]) => {
                // Find the latest version whose version_date is within 1 minute of the selectedItem.updated_date
                const selectedItemTime = selectedItem.updated_date ? new Date(selectedItem.updated_date).getTime() : 0;
                const oneMinuteInMs = 60 * 1000;

                // Filter versions within 1 minute and sort by version_date descending
                const matchingVersions = versions
                    .filter((version) => {
                        const versionTime = new Date(version.version_date).getTime();
                        return Math.abs(versionTime - selectedItemTime) <= oneMinuteInMs;
                    })
                    .sort((a, b) => new Date(b.version_date).getTime() - new Date(a.version_date).getTime());

                // Get the latest matching version
                const matchingVersion = matchingVersions.length > 0 ? matchingVersions[0] : null;

                if (matchingVersion) {
                    dashboardLLMCache.get(EXPLAIN_DDX, patient, tenant, matchingVersion.version_id)
                        .then((data: any) => {
                            setDDX(data);
                        });
                    dashboardLLMCache.get(SCAN_MULTI, patient, tenant, matchingVersion.source_version_id)
                        .then((data: any) => {
                            setScanMulti(data);
                        });
                }
            });
        }
    }, [selectedItem, showModal]);

    const filteredItems: ActionableItem[] = items.filter((item: ActionableItem) =>
        filter === "all" ? true : item.status === filter
    );

    const getPriorityValue = (priority?: Priority): number => {
        switch(priority) {
            case "HIGH": return 3;
            case "MEDIUM": return 2;
            case "LOW": return 1;
            default: return 0;
        }
    };

    const sortedItems = [...filteredItems].sort((a: ActionableItem, b: ActionableItem) => {
        switch(sortBy) {
            case 'priority':
                return getPriorityValue(b.priority) - getPriorityValue(a.priority);
            case 'type':
                return (a.item_type || '').localeCompare(b.item_type || '');
            case 'date':
            default:
                return (b.creation_date ? new Date(b.creation_date).getTime() : 0) - (a.creation_date ? new Date(a.creation_date).getTime() : 0);
        }
    });

    const handleStatusChange = async (itemId: number, newStatus: Status): Promise<void> => {
        const now = new Date();
        
        // Optimistic update
        setItems(items.map((item: ActionableItem) => {
            if (item.id === itemId) {
                return {
                    ...item,
                    status: newStatus,
                    updated_date: now,
                };
            }
            return item;
        }));
        if (selectedItem && selectedItem.id === itemId) {
            setSelectedItem({
                ...selectedItem,
                status: newStatus,
                updated_date: now,
            });
        }

        // Persist to backend
        try {
            await callWrite({
                operation: UPDATE_ACTIONABLE_ITEM_STATUS,
                id: itemId,
                status: newStatus
            });
            console.log(`Updated item ${itemId} status to ${newStatus}`);
        } catch (error) {
            console.error('Error updating status:', error);
            // Optionally: revert optimistic update or show error message
        }
    };

    const handleAssigneeChange = async (newAssignee: AssigneeType | null): Promise<void> => {
        if (!selectedItem || !selectedItem.id) return;
        
        const now = new Date();
        const itemId = selectedItem.id;
        
        // Optimistic update
        setItems(items.map((item: ActionableItem) => {
            if (item.id === itemId) {
                return {
                    ...item,
                    executor_role: newAssignee || undefined,
                    updated_date: now
                };
            }
            return item;
        }));
        setSelectedItem({
            ...selectedItem,
            executor_role: newAssignee || undefined,
            updated_date: now
        });
        setSelectedAssignee(newAssignee);

        // Persist to backend
        try {
            await callWrite({
                operation: UPSERT_ACTIONABLE_ITEM,
                id: itemId,
                csn: selectedItem.csn,
                mrn: selectedItem.mrn,
                body: selectedItem.body,
                item_type: selectedItem.item_type,
                status: selectedItem.status,
                priority: selectedItem.priority,
                category: selectedItem.category,
                clinical_scope: selectedItem.clinical_scope,
                supporting_context: selectedItem.supporting_context,
                author: selectedItem.author,
                executor_role: newAssignee || undefined,
            });
            console.log(`Updated item ${itemId} assignee to ${newAssignee}`);
        } catch (error) {
            console.error('Error updating assignee:', error);
            // Optionally: revert optimistic update or show error message
        }
    };

    const handleAddAnswer = async (): Promise<void> => {
        if (!newAnswerText.trim() || !selectedItem?.id) return;
        
        const newAnswer: ActionableAnswer = {
            actionable_item_id: selectedItem.id,
            answer_payload: newAnswerText,
            answer_source: 'MANUAL_ENTRY',
            creation_date: new Date(),
            updated_date: new Date(),
        };
        
        // Optimistic update
        setAnswers([...answers, newAnswer]);
        setNewAnswerText("");
        
        // Persist to backend
        try {
            const result = await callWrite({
                operation: INSERT_ACTIONABLE_ANSWER,
                actionable_item_id: selectedItem.id,
                answer_payload: newAnswerText,
                answer_source: 'MANUAL_ENTRY' as AnswerSource
            });
            console.log(`Added answer to item ${selectedItem.id}`);
            
            // Refresh answers to get the ID from backend
            if (selectedItem.id) {
                await fetchAnswers(selectedItem.id);
            }
        } catch (error) {
            console.error('Error adding answer:', error);
            // Revert optimistic update
            setAnswers(answers);
            setNewAnswerText(newAnswer.answer_payload);
        }
    };

    const fetchAnswers = async (itemId: number): Promise<void> => {
        setLoadingAnswers(true);
        try {
            const fetchedAnswers = await callQuery({
                operation: ACTIONABLE_ANSWERS_BY_ITEM_ID,
                actionable_item_id: itemId,
            });
            setAnswers(fetchedAnswers || []);
        } catch (error) {
            console.error('Error fetching answers:', error);
            setAnswers([]);
        } finally {
            setLoadingAnswers(false);
        }
    };

    const openModal = (item: ActionableItem): void => {
        setSelectedItem(item);
        setShowModal(true);
        setSelectedAssignee(item.executor_role || null);
        setNewAnswerText("");
        setAnswers([]);
        
        if (item.id) {
            fetchAnswers(item.id);
        }
    };

    const closeModal = (): void => {
        setShowModal(false);
        setSelectedItem(null);
        setSelectedAssignee(null);
        setNewAnswerText("");
        setAnswers([]);
    };

    const formatDate = (date?: Date): string => {
        if (!date) return 'N/A';
        const d = new Date(date);
        return d.toLocaleString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const getPriorityClass = (priority?: Priority): string => {
        switch(priority) {
            case "HIGH": return "priority-high";
            case "MEDIUM": return "priority-medium";
            case "LOW": return "priority-low";
            default: return "";
        }
    };

    const getStatusIcon = (status?: Status) => {
        switch(status) {
            case "COMPLETED": return <FontAwesomeIcon icon={faCheckCircle} />;
            case "ARCHIVED": return <FontAwesomeIcon icon={faArchive} />;
            default: return <FontAwesomeIcon icon={faCircle} />;
        }
    };

    const getStatusClass = (status?: Status): string => {
        switch(status) {
            case "COMPLETED": return "status-completed";
            case "IN_PROGRESS": return "status-in-progress";
            case "ARCHIVED": return "status-cancelled";
            default: return "status-pending";
        }
    };

    const getAnswerSourceLabel = (source?: AnswerSource): string => {
        switch(source) {
            case 'LIDIA_SMS': return 'LIDIA (SMS)';
            case 'RESULT': return 'System Result';
            case 'MANUAL_ENTRY': return 'Manual Entry';
            default: return 'Unknown';
        }
    };

    const getAnswerSourceIcon = (source?: AnswerSource) => {
        switch(source) {
            case 'LIDIA_SMS': return <FontAwesomeIcon icon={faRobot} />;
            case 'RESULT': return <FontAwesomeIcon icon={faChartLine} />;
            case 'MANUAL_ENTRY': return <FontAwesomeIcon icon={faPencilAlt} />;
            default: return <FontAwesomeIcon icon={faComment} />;
        }
    };

    /**
     * The actionable items come from the database. However, if an item is completed, it may reference citations 
     * whose source we cannot identity. In these cases, we replace the context with the context from the latest
     * DDX which will have the linked scan multi context.
     */
    const getActionableContext = () => {
        if (selectedItem && ddx && ddx.actionable_items && selectedItem.supporting_context && scanMulti) {
            for (let actionableItem of ddx.actionable_items) {
                // console.log(`Found candidate actionable item`, selectedItem.id, actionableItem);
                if (`${selectedItem.id}` === `${actionableItem.previous_id}` && actionableItem.supporting_context) {
                    // console.log(`Found matching actionable item`, actionableItem);
                    return <LLMDashboardMarkdownRenderer content={actionableItem.supporting_context} scanMulti={scanMulti} />;
                }
            }
        } else if (selectedItem && selectedItem.supporting_context) {
            return <>{selectedItem.supporting_context}</>;
        }
        return <></>;
    }

    return (
        <div className="actionable-intelligence-tracker mb-4">
            <div className="container">
                {/* Search and Filters */}
                <div className="ai-filter-container mb-3">
                    <div className="ai-filters">
                        <button
                            className={`filter-btn ${filter === "OPEN" ? "active" : ""}`}
                            onClick={() => setFilter("OPEN")}
                        >
                            <FontAwesomeIcon icon={faCircle} />
                            {items.filter((i: ActionableItem) => i.status === "OPEN").length} Open
                        </button>
                        <button
                            className={`filter-btn ${filter === "IN_PROGRESS" ? "active" : ""}`}
                            onClick={() => setFilter("IN_PROGRESS")}
                        >
                            <FontAwesomeIcon icon={faGear} />
                            {items.filter((i: ActionableItem) => i.status === "IN_PROGRESS").length} In Progress
                        </button>
                        <button
                            className={`filter-btn ${filter === "COMPLETED" ? "active" : ""}`}
                            onClick={() => setFilter("COMPLETED")}
                        >
                            <FontAwesomeIcon icon={faCheckCircle} />
                            {items.filter((i: ActionableItem) => i.status === "COMPLETED").length} Completed
                        </button>
                        <button
                            className={`filter-btn ${filter === "ARCHIVED" ? "active" : ""}`}
                            onClick={() => setFilter("ARCHIVED")}
                        >
                            <FontAwesomeIcon icon={faArchive} />
                            {items.filter((i: ActionableItem) => i.status === "ARCHIVED").length} Archived
                        </button>
                        
                        <div className="sort-divider"></div>
                        <div className="sort-controls">
                            <span className="sort-label">Sort by:</span>
                            <button
                                className={`filter-btn ${sortBy === "date" ? "active" : ""}`}
                                onClick={() => setSortBy("date")}
                            >
                                Date
                            </button>
                            <button
                                className={`filter-btn ${sortBy === "priority" ? "active" : ""}`}
                                onClick={() => setSortBy("priority")}
                            >
                                Priority
                            </button>
                            <button
                                className={`filter-btn ${sortBy === "type" ? "active" : ""}`}
                                onClick={() => setSortBy("type")}
                            >
                                Type
                            </button>
                        </div>
                    </div>
                </div>

                {/* Items List */}
                <div className="ai-list">
                    {sortedItems.map((item: ActionableItem) => (
                        <div
                            key={item.id}
                            className="ai-item"
                            onClick={() => openModal(item)}
                        >
                            <div className="d-flex align-items-start">
                                <div className="flex-grow-1">
                                    <div className="ai-content">
                                        {item.body}
                                    </div>
                                    <div className="ai-meta">
                                        <span className={`ai-type-badge ${item.item_type === "TASK" ? "type-task" : "type-question"}`}>
                                            {item.item_type === "TASK" ? <FontAwesomeIcon icon={faTasks} /> : <FontAwesomeIcon icon={faQuestionCircle} />}
                                            {item.item_type}
                                        </span>
                                        {item.priority && (
                                            <span className={`ai-priority-badge ${getPriorityClass(item.priority)}`}>
                                                {item.priority}
                                            </span>
                                        )}
                                        {item.category && (
                                            <span className="badge bg-secondary">{item.category}</span>
                                        )}
                                        <span> • </span>
                                        <span><FontAwesomeIcon icon={faClock} /> {formatDate(item.creation_date)}</span>
                                        {item.executor_role && (
                                            <span> • <span className="ms-1"><FontAwesomeIcon icon={faUser} /></span> {item.executor_role}</span>
                                        )}
                                        {item.author && (
                                            <span> • <span className="ms-1"><FontAwesomeIcon icon={faCodeBranch} /></span> {item.clinical_scope}</span>
                                        )}
                                        {item.id && (
                                            <span> • <span className="ms-1"><FontAwesomeIcon icon={faIdCard} /></span> {item.id}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* React Bootstrap Modal */}
                <Modal show={showModal} onHide={closeModal} size="lg" centered className='actionable-intelligence-tracker'>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <span className={`ai-type-badge ${selectedItem?.item_type === "TASK" ? "type-task" : "type-question"} me-2`}>
                                {selectedItem?.item_type === "TASK" ? <FontAwesomeIcon icon={faTasks} /> : <FontAwesomeIcon icon={faQuestionCircle} />}
                                {selectedItem?.item_type}
                            </span>
                            <span className={`status-badge ${getStatusClass(selectedItem?.status)}`}>
                                {getStatusIcon(selectedItem?.status)}
                                {selectedItem?.status}
                            </span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {loadingAnswers ? (
                            <div className="text-center py-3">
                                <FontAwesomeIcon icon={faGear} spin /> Loading...
                            </div>
                        ) : selectedItem ? (
                            <>
                                {/* Original Item - GitHub Issue Style */}
                                <div className="github-comment-container mb-3">
                                    <div className="github-comment-header">
                                        <span>
                                            <FontAwesomeIcon icon={faUserCircle} /> <strong>{selectedItem.author || 'System'}</strong>{' '}
                                            <span className="text-muted">opened this {selectedItem.item_type?.toLowerCase()}</span> {' '}
                                            <span className="text-muted"> {' '}
                                                <PersonalIdentifier identifier={selectedItem.creation_date} isTime />
                                            </span>
                                        </span>
                                    </div>
                                    <div className="github-comment">
                                        {/* <div className="mb-2">
                                            <span className={`ai-type-badge ${selectedItem?.item_type === "TASK" ? "type-task" : "type-question"} me-2`}>
                                                <i className={`fas ${selectedItem?.item_type === "TASK" ? "fa-tasks" : "fa-question-circle"}`}></i>
                                                {selectedItem?.item_type}
                                            </span>
                                        </div> */}
                                        <div className="comment-body">
                                            <p className="mb-2">{selectedItem.body}</p>
                                            <hr />
                                            {selectedItem.supporting_context && (
                                                <div className="mb-2">
                                                    <span className="text-muted"><small><strong>Context</strong> {getActionableContext()}</small></span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="comment-footer">
                                            <Row>
                                                <Col>
                                                    {selectedItem.priority && (
                                                        <div className="detail-row">
                                                            <span className="detail-label"><FontAwesomeIcon icon={faExclamationCircle} /> Priority:</span>
                                                            <span className={`ai-priority-badge ${getPriorityClass(selectedItem.priority)}`}>
                                                                {selectedItem.priority}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {selectedItem.clinical_scope && (
                                                        <div className="detail-row">
                                                            <span className="detail-label"><FontAwesomeIcon icon={faStethoscope} /> Scope:</span>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip>
                                                                        {selectedItem.clinical_scope === 'ACUTE' && 'ACUTE: For current visit'}
                                                                        {selectedItem.clinical_scope === 'SUBACUTE' && 'SUBACUTE: For post-discharge/short-term'}
                                                                        {selectedItem.clinical_scope === 'CHRONIC' && 'CHRONIC: For long-term management'}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                                                                    {selectedItem.clinical_scope}
                                                                </span>
                                                            </OverlayTrigger>
                                                        </div>
                                                    )}
                                                </Col>
                                                <Col>
                                                    {selectedItem.category && (
                                                        <div className="detail-row">
                                                            <span className="detail-label"><FontAwesomeIcon icon={faTag} /> Category:</span>
                                                            <span>{selectedItem.category}</span>
                                                        </div>
                                                    )}
                                                    <div className="detail-row">
                                                        <span className="detail-label"><FontAwesomeIcon icon={faUser} /> Assigned to:</span>
                                                        <select 
                                                            className="form-select form-select-sm d-inline-block w-auto"
                                                            style={{marginTop: '-4px'}}
                                                            value={selectedAssignee || ""}
                                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                                                                handleAssigneeChange(e.target.value as AssigneeType || null)
                                                            }
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <option value="">Unassigned</option>
                                                            {assigneeOptions.map((option) => (
                                                                <option key={option} value={option}>
                                                                    {option}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </Col>
                                            </Row>
                                            {/* <div className="detail-row">
                                                <span className="detail-label"><FontAwesomeIcon icon={faIdCard} /> CSN:</span>
                                                <span>{selectedItem.csn}</span>
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label"><FontAwesomeIcon icon={faHospitalUser} /> MRN:</span>
                                                <span>{selectedItem.mrn}</span>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>

                                {/* Answers Section - GitHub Comments Style */}
                                <div className="github-comments-section">
                                    {!loadingAnswers && answers.length > 0 && (
                                        <div className="mb-3">
                                            {answers.map((answer, index) => (
                                                <div key={answer.id || index} className="github-comment-container mb-2">
                                                    <div className="github-comment-header">
                                                        <span>
                                                            {getAnswerSourceIcon(answer.answer_source)}{' '}
                                                            <strong>{getAnswerSourceLabel(answer.answer_source)}</strong>{' '}
                                                            <span className="text-muted">commented</span>{' '}
                                                            <span className="text-muted" title={formatDate(answer.creation_date)}>
                                                                <PersonalIdentifier identifier={answer.creation_date} isTime />
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <div className="github-comment">
                                                        <div className="comment-body">
                                                            <p className="mb-0">{answer.answer_payload}</p>
                                                        </div>
                                                        {
                                                            answer.answer_source === 'LIDIA_SMS' && (
                                                                <div className="mt-2">
                                                                    <Button variant="outline-secondary" size="sm" onClick={() => {
                                                                        window.open(`/dashboard/${patient.internal_csn}/lidia`, "_blank");
                                                                    }}><FontAwesomeIcon icon={faArrowUpRightFromSquare} /> See SMS Conversation</Button>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* {!loadingAnswers && answers.length === 0 && (
                                        <div className="text-center text-muted py-3">
                                            <FontAwesomeIcon icon={faComments} /> No answers yet
                                        </div>
                                    )} */}

                                    {/* Add New Answer */}
                                    <div className="mt-4">
                                        <div>
                                            <div className="comment-header mb-2">
                                                <div className="d-flex align-items-center gap-2">
                                                    <FontAwesomeIcon icon={faUserCircle} />
                                                    Add a comment
                                                </div>
                                            </div>
                                            <div className="comment-body mb-2">
                                                <textarea
                                                    className="form-control"
                                                    rows={3}
                                                    placeholder="Write your answer or comment..."
                                                    value={newAnswerText}
                                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewAnswerText(e.target.value)}
                                                ></textarea>
                                            </div>
                                            <div className="comment-footer d-flex justify-content-end">
                                                <Button 
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={handleAddAnswer}
                                                    disabled={!newAnswerText.trim()}
                                                >
                                                    <FontAwesomeIcon icon={faComment} />{' '}
                                                    Comment
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions Section */}
                                <div className="ai-detail-section mt-3">
                                    <div className="d-flex gap-2 flex-wrap">
                                        {selectedItem.status !== "COMPLETED" && selectedItem.id && (
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={() => handleStatusChange(selectedItem.id!, "COMPLETED")}
                                            >
                                                <FontAwesomeIcon icon={faCheck} />{' '}
                                                Mark Complete
                                            </Button>
                                        )}
                                        {selectedItem.status === "COMPLETED" && selectedItem.id && (
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleStatusChange(selectedItem.id!, "OPEN")}
                                            >
                                                <FontAwesomeIcon icon={faUndo} />{' '}
                                                Reopen
                                            </Button>
                                        )}
                                        {selectedItem.status === "OPEN" && selectedItem.id && (
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleStatusChange(selectedItem.id!, "IN_PROGRESS")}
                                            >
                                                <FontAwesomeIcon icon={faPlay} />{' '}
                                                Start Progress
                                            </Button>
                                        )}
                                        {selectedItem.status !== "ARCHIVED" && selectedItem.id && (
                                            <Button
                                                variant="light"
                                                size="sm"
                                                onClick={() => handleStatusChange(selectedItem.id!, "ARCHIVED")}
                                            >
                                                <FontAwesomeIcon icon={faArchive} />{' '}
                                                Archive
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : <></>}
                    </Modal.Body>
                </Modal>
            </div>
        </div>
    );
};

export default ActionableIntelligenceTracker;
